import { computed } from "vue";
import { useUsers } from "@/Composables/useUsers";
import { useCategories } from "@/Composables/useCategories";
import { useBrands } from "@/Composables/useBrands";
import { useIntervals } from "@/Composables/useIntervals";
import { useSuppliers } from "@/Composables/useSuppliers";
import { useTags } from "@/Composables/useTags";
import { sortBy } from "lodash";
import { useI18n } from "vue-i18n";
import { addDays, endOfDay, startOfDay, subDays, subYears } from "date-fns";

export { default as FilterSingle } from "./FilterSingle.vue";

export const getShortcutName = (shortcut) => {
    const { t } = useI18n();
    return t("components.inputs.datepicker.shortcuts." + shortcut);
};

export const FilterType = Object.freeze({
    STATUS: "status",
    LINKED_TO: "linked_to",
    ASSIGNED_TO: "assigned_to",
    EXPIRATION_DATE: "expiration_date",
    NOTIFICATION_DATE: "notification_date",
    DUE_DATE: "due_date",
    COMPLETED_DATE: "completed_date",
    CREATED_DATE: "created_date",
    PURCHASED_DATE: "purchased_date",
    VISIBILITY: "visibility",
    PRIORITY: "priority",
    CATEGORY: "category",
    BRAND: "brand",
    SUPPLIER: "supplier",
    TAGS: "tags",
    TASK_TYPE: "task_type",
    ASSET_GROUPS: "asset_groups",
    RECURRENCE: "recurrence",
});

export const DateShortcut = Object.freeze({
    TODAY: "today",
    YESTERDAY: "yesterday",
    TOMORROW: "tomorrow",
    NEXT_7_DAYS: "next_7_days",
    NEXT_30_DAYS: "next_30_days",
    NEXT_12_MONTHS: "next_12_months",
    LAST_7_DAYS: "last_7_days",
    LAST_30_DAYS: "last_30_days",
    LAST_12_MONTHS: "last_12_months",
    CUSTOM: "custom",
    OVERDUE: "overdue",
    NO_DUE_DATE: "no_due_date",
    NO_COMPLETED_DATE: "no_completed_date",
    NO_EXPIRATION_DATE: "no_expiration_date",
    NO_NOTIFICATION_DATE: "no_notification_date",
    EXPIRED: "expired",
});

export const VisibilityType = Object.freeze({
    PRIVATE: "private",
    PUBLIC: "public",
});

export const TaskTypes = Object.freeze({
    NON_RECURRING: "non_recurring",
    RECURRING: "recurring",
});

export const StatusTypes = Object.freeze({
    OPEN: "open",
    SCHEDULED: "scheduled",
    COMPLETED: "completed",
});

export function isDateFilter(filterType) {
    return [
        FilterType.EXPIRATION_DATE,
        FilterType.NOTIFICATION_DATE,
        FilterType.DUE_DATE,
        FilterType.COMPLETED_DATE,
        FilterType.CREATED_DATE,
    ].includes(filterType);
}

export function useFilterOptions(tenantIds, page, type) {
    const { t } = useI18n();

    const { isLoading: isLoadingUsers, data: assigneesData } = useUsers(
        tenantIds,
        type === "recurring_tasks" ? "all" : "any"
    );
    const { isLoading: isLoadingCategories, data: categoriesData } =
        useCategories(tenantIds, type === "recurring_tasks" ? "all" : "any");

    const { isLoading: isLoadingTags, data: tagsData } = useTags(
        tenantIds,
        "any"
    );
    const { isLoading: isLoadingBrands, data: brandsData } = useBrands(
        tenantIds,
        "any"
    );
    const { isLoading: isLoadingSuppliers, data: suppliersData } = useSuppliers(
        tenantIds,
        "any"
    );

    const { intervals } = useIntervals();

    const filterOptions = computed(() => {
        switch (type) {
            case "files":
                return [
                    {
                        label: t("components.filters.visibility.label"),
                        multiple: false,
                        type: "single",
                        id: "visibility",
                        options: visibilities.value,
                    },
                    {
                        label: t("components.filters.tags.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "tags",
                        options: tags.value,
                    },
                    {
                        label: t("components.filters.created_date.label"),
                        multiple: false,
                        type: "date",
                        id: "created_date",
                        options: createdDateShortcuts.value,
                    },
                    {
                        label: t("components.filters.expiration_date.label"),
                        multiple: false,
                        type: "date",
                        id: "expiration_date",
                        options: expirationDateShortcuts.value,
                    },
                    {
                        label: t("components.filters.notification_date.label"),
                        multiple: false,
                        type: "date",
                        id: "reminder_date",
                        options: notificationDateShortcuts.value,
                    },
                ];
            case "assets":
                return [
                    {
                        label: t("components.filters.category.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "category",
                        options: categories.value,
                    },
                    {
                        label: t("components.filters.brand.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "brand",
                        options: brands.value,
                    },
                    {
                        label: t("components.filters.supplier.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "supplier",
                        options: suppliers.value,
                    },
                    {
                        label: t("components.filters.tags.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "tags",
                        options: tags.value,
                    },
                    {
                        label: t("components.filters.created_date.label"),
                        multiple: false,
                        type: "date",
                        id: "created_date",
                        options: createdDateShortcuts.value,
                    },
                    {
                        label: t("components.filters.purchased_date.label"),
                        multiple: false,
                        type: "date",
                        id: "purchased_date",
                        options: purchasedDateShortcuts.value,
                    },
                ];
            case "recurring_tasks":
                return [
                    {
                        label: t("components.filters.category.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "category",
                        options: categories.value,
                    },
                    {
                        label: t("components.filters.assigned_to.label"),
                        multiple: true,
                        type: "avatar",
                        id: "assigned_to",
                        options: assignees.value,
                    },
                    {
                        label: t("components.filters.priority.label"),
                        multiple: true,
                        type: "dot",
                        id: "priority",
                        options: priorities.value,
                    },
                    {
                        label: t("components.filters.recurrence.label"),
                        multiple: false,
                        type: "checkbox",
                        id: "recurrence",
                        options: intervals.value,
                    },
                ];
            default:
                // Tasks
                return [
                    {
                        label: t("components.filters.status.label"),
                        multiple: true,
                        type: "dot",
                        id: "status",
                        options: statuses.value,
                    },
                    {
                        label: t("components.filters.assigned_to.label"),
                        multiple: true,
                        type: "avatar",
                        id: "assigned_to",
                        options: assignees.value,
                    },
                    {
                        label: t("components.filters.priority.label"),
                        multiple: true,
                        type: "dot",
                        id: "priority",
                        options: priorities.value,
                    },
                    {
                        label: t("components.filters.category.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "category",
                        options: categories.value,
                    },
                    {
                        label: t("components.filters.tags.label"),
                        multiple: true,
                        type: "checkbox",
                        id: "tags",
                        options: tags.value,
                    },
                    {
                        label: t("components.filters.visibility.label"),
                        multiple: false,
                        type: "single",
                        id: "visibility",
                        options: visibilities.value,
                    },
                    {
                        label: t("components.filters.task_type.label"),
                        multiple: false,
                        type: "single",
                        id: "task_type",
                        options: taskTypes.value,
                    },
                    {
                        label: t("components.filters.created_date.label"),
                        multiple: false,
                        type: "date",
                        id: "created_date",
                        options: createdDateShortcuts.value,
                    },
                    {
                        label: t("components.filters.due_date.label"),
                        multiple: false,
                        type: "date",
                        id: "due_date",
                        options: dueDateShortcuts.value,
                    },
                    {
                        label: t("components.filters.completed_date.label"),
                        multiple: false,
                        type: "date",
                        id: "completed_date",
                        options: completedDateShortcuts.value,
                    },
                ];
        }
    });

    const statuses = computed(() => {
        return [
            {
                id: StatusTypes.OPEN,
                label: t("components.filters.status.open"),
            },
            {
                id: StatusTypes.SCHEDULED,
                label: t("components.filters.status.scheduled"),
            },
            {
                id: StatusTypes.COMPLETED,
                label: t("components.filters.status.completed"),
            },
        ];
    });

    const assignees = computed(() => {
        if (isLoadingUsers.value) {
            return [];
        }

        return [
            {
                id: "unassigned",
                label: t("components.filters.assigned_to.unassigned"),
            },
        ]
            .concat(sortBy(assigneesData.value, "fullname"))
            .map((user) => {
                let { fullname, ...other } = user;
                return { label: fullname, ...other };
            });
    });

    const priorities = computed(() => {
        return page.props.priorities.map((priority) => {
            let { name, ...other } = priority;
            return { label: name, ...other };
        });
    });

    const categories = computed(() => {
        if (isLoadingCategories.value) {
            return [[]];
        }

        return [
            {
                id: "no_category",
                label: t("components.filters.category.no_category"),
            },
        ]
            .concat(
                tenantIds.length === 1
                    ? categoriesData.value[tenantIds[0]]
                    : categoriesData.value
            )
            .map((category) => {
                let { name, ...other } = category;
                return { label: name, ...other };
            });
    });

    const tags = computed(() => {
        if (isLoadingTags.value) {
            return [[]];
        }

        return [
            {
                id: "no_tags",
                label: t("components.filters.tags.no_tags"),
            },
        ]
            .concat(tagsData.value)
            .map((tag) => {
                let { name, ...other } = tag;
                return { label: name, ...other };
            });
    });

    const brands = computed(() => {
        if (isLoadingBrands.value) {
            return [[]];
        }

        const items =
            tenantIds.length === 1
                ? brandsData.value[tenantIds[0]]
                    ? brandsData.value[tenantIds[0]]
                    : []
                : brandsData.value[0].brands
                ? brandsData.value[0].brands
                : [];

        return [
            {
                id: "no_brand",
                label: t("components.filters.brand.no_brand"),
            },
        ]
            .concat(items)
            .map((item) => {
                let { brand, ...other } = item;
                return { label: brand, id: brand, ...other };
            })
            .filter(
                (item, index, self) =>
                    index === self.findIndex((t) => t.label === item.label)
            );
    });

    const suppliers = computed(() => {
        if (isLoadingSuppliers.value) {
            return [[]];
        }

        const items =
            tenantIds.length === 1
                ? suppliersData.value[tenantIds[0]]
                    ? suppliersData.value[tenantIds[0]]
                    : []
                : suppliersData.value[0].suppliers
                ? suppliersData.value[0].suppliers
                : [];

        return [
            {
                id: "no_supplier",
                label: t("components.filters.supplier.no_supplier"),
            },
        ]
            .concat(items)
            .map((item) => {
                let { supplier, ...other } = item;
                return { label: supplier, id: supplier, ...other };
            })
            .filter(
                (item, index, self) =>
                    index === self.findIndex((t) => t.label === item.label)
            );
    });

    const visibilities = computed(() => {
        return [
            {
                id: VisibilityType.PRIVATE,
                label: t("components.filters.visibility.private"),
            },
            {
                id: VisibilityType.PUBLIC,
                label: t("components.filters.visibility.public"),
            },
        ];
    });

    const taskTypes = computed(() => {
        return [
            {
                id: TaskTypes.NON_RECURRING,
                label: t("components.filters.task_type.non_recurring"),
            },
            {
                id: TaskTypes.RECURRING,
                label: t("components.filters.task_type.recurring"),
            },
        ];
    });

    const createdDateShortcuts = computed(() => {
        return [
            {
                id: DateShortcut.TODAY,
                label: getShortcutName(DateShortcut.TODAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.YESTERDAY,
                label: getShortcutName(DateShortcut.YESTERDAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 1)),
                        end: endOfDay(subDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.LAST_7_DAYS,
                label: getShortcutName(DateShortcut.LAST_7_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 7)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.LAST_30_DAYS,
                label: getShortcutName(DateShortcut.LAST_30_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 30)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.LAST_12_MONTHS,
                label: getShortcutName(DateShortcut.LAST_12_MONTHS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subYears(date, 1)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.CUSTOM,
                label: getShortcutName(DateShortcut.CUSTOM),
                range: () => {},
            },
        ];
    });

    const expirationDateShortcuts = computed(() => {
        return [
            {
                id: DateShortcut.NO_EXPIRATION_DATE,
                label: getShortcutName(DateShortcut.NO_EXPIRATION_DATE),
                range: () => {
                    return {
                        start: null,
                        end: null,
                    };
                },
            },
            {
                id: DateShortcut.EXPIRED,
                label: getShortcutName(DateShortcut.EXPIRED),
                range: () => {
                    const date = new Date();

                    return {
                        start: null,
                        end: endOfDay(subDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.TODAY,
                label: getShortcutName(DateShortcut.TODAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.TOMORROW,
                label: getShortcutName(DateShortcut.TOMORROW),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(addDays(date, 1)),
                        end: endOfDay(addDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.NEXT_7_DAYS,
                label: getShortcutName(DateShortcut.NEXT_7_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(addDays(date, 7)),
                    };
                },
            },
            {
                id: DateShortcut.NEXT_30_DAYS,
                label: getShortcutName(DateShortcut.NEXT_30_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(addDays(date, 30)),
                    };
                },
            },
            {
                id: DateShortcut.CUSTOM,
                label: getShortcutName(DateShortcut.CUSTOM),
                range: () => {},
            },
        ];
    });

    const notificationDateShortcuts = computed(() => {
        return [
            {
                id: DateShortcut.NO_NOTIFICATION_DATE,
                label: getShortcutName(DateShortcut.NO_NOTIFICATION_DATE),
                range: () => {
                    return {
                        start: null,
                        end: null,
                    };
                },
            },
            {
                id: DateShortcut.TODAY,
                label: getShortcutName(DateShortcut.TODAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.TOMORROW,
                label: getShortcutName(DateShortcut.TOMORROW),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(addDays(date, 1)),
                        end: endOfDay(addDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.NEXT_7_DAYS,
                label: getShortcutName(DateShortcut.NEXT_7_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(addDays(date, 7)),
                    };
                },
            },
            {
                id: DateShortcut.NEXT_30_DAYS,
                label: getShortcutName(DateShortcut.NEXT_30_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(addDays(date, 30)),
                    };
                },
            },
            {
                id: DateShortcut.CUSTOM,
                label: getShortcutName(DateShortcut.CUSTOM),
                range: () => {},
            },
        ];
    });

    const dueDateShortcuts = computed(() => {
        return [
            {
                id: DateShortcut.NO_DUE_DATE,
                label: getShortcutName(DateShortcut.NO_DUE_DATE),
                range: () => {
                    return {
                        start: null,
                        end: null,
                    };
                },
            },
            {
                id: DateShortcut.OVERDUE,
                label: getShortcutName(DateShortcut.OVERDUE),
                range: () => {
                    const date = new Date();

                    return {
                        start: null,
                        end: endOfDay(subDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.TODAY,
                label: getShortcutName(DateShortcut.TODAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.TOMORROW,
                label: getShortcutName(DateShortcut.TOMORROW),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(addDays(date, 1)),
                        end: endOfDay(addDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.NEXT_7_DAYS,
                label: getShortcutName(DateShortcut.NEXT_7_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(addDays(date, 7)),
                    };
                },
            },
            {
                id: DateShortcut.NEXT_30_DAYS,
                label: getShortcutName(DateShortcut.NEXT_30_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(addDays(date, 30)),
                    };
                },
            },
            {
                id: DateShortcut.CUSTOM,
                label: getShortcutName(DateShortcut.CUSTOM),
                range: () => {},
            },
        ];
    });

    const completedDateShortcuts = computed(() => {
        return [
            {
                id: DateShortcut.NO_COMPLETED_DATE,
                label: getShortcutName(DateShortcut.NO_COMPLETED_DATE),
                range: () => {
                    return {
                        start: null,
                        end: null,
                    };
                },
            },
            {
                id: DateShortcut.TODAY,
                label: getShortcutName(DateShortcut.TODAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.YESTERDAY,
                label: getShortcutName(DateShortcut.YESTERDAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 1)),
                        end: endOfDay(subDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.LAST_7_DAYS,
                label: getShortcutName(DateShortcut.LAST_7_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 7)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.LAST_30_DAYS,
                label: getShortcutName(DateShortcut.LAST_30_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 30)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.LAST_12_MONTHS,
                label: getShortcutName(DateShortcut.LAST_12_MONTHS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subYears(date, 1)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.CUSTOM,
                label: getShortcutName(DateShortcut.CUSTOM),
                range: () => {},
            },
        ];
    });

    const purchasedDateShortcuts = computed(() => {
        return [
            {
                id: DateShortcut.TODAY,
                label: getShortcutName(DateShortcut.TODAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(date),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.YESTERDAY,
                label: getShortcutName(DateShortcut.YESTERDAY),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 1)),
                        end: endOfDay(subDays(date, 1)),
                    };
                },
            },
            {
                id: DateShortcut.LAST_7_DAYS,
                label: getShortcutName(DateShortcut.LAST_7_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 7)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.LAST_30_DAYS,
                label: getShortcutName(DateShortcut.LAST_30_DAYS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subDays(date, 30)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.LAST_12_MONTHS,
                label: getShortcutName(DateShortcut.LAST_12_MONTHS),
                range: () => {
                    const date = new Date();

                    return {
                        start: startOfDay(subYears(date, 1)),
                        end: endOfDay(date),
                    };
                },
            },
            {
                id: DateShortcut.CUSTOM,
                label: getShortcutName(DateShortcut.CUSTOM),
                range: () => {},
            },
        ];
    });

    return {
        filterOptions,
        statuses,
        assignees,
        priorities,
        categories,
        tags,
        visibilities,
        taskTypes,
        createdDateShortcuts,
        expirationDateShortcuts,
        notificationDateShortcuts,
        dueDateShortcuts,
        completedDateShortcuts,
        purchasedDateShortcuts,
    };
}
